import {
  type InfiniteData,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'
import type { UserNotificationsLDResponse } from '~/api/queries/user'
import { fetchApiWithToken } from '~/api/utils'

type ReadNotificationPayload = {
  notificationId: number
}

export const useReadNotificationMutation = ({ userId }: { userId: number }) => {
  const queryClient = useQueryClient()
  return useMutation<
    boolean,
    Error,
    ReadNotificationPayload,
    { previousNotifications?: InfiniteData<UserNotificationsLDResponse> }
  >({
    mutationFn: ({ notificationId }: ReadNotificationPayload) =>
      fetchApiWithToken({
        method: 'PATCH',
        url: `/v1/notifications/${notificationId}`,
        data: {
          isRead: true
        }
      }),
    onMutate: async ({ notificationId }) => {
      await queryClient.cancelQueries({
        queryKey: ['user', 'notifications', userId]
      })
      const previousNotifications = queryClient.getQueryData<
        InfiniteData<UserNotificationsLDResponse, Error>
      >(['user', 'notifications', userId])
      if (previousNotifications) {
        const updatedNotifications = previousNotifications.pages.map(
          (page) => ({
            ...page,
            'hydra:member': page['hydra:member'].map((notification) =>
              notification.id === notificationId
                ? { ...notification, isRead: true }
                : notification
            )
          })
        )
        queryClient.setQueryData(['user', 'notifications', userId], {
          pages: updatedNotifications,
          pageParams: previousNotifications.pageParams
        })
      }
      return { previousNotifications }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['user', 'notifications', userId]
      }),
    onError: (_error, _variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['user', 'notifications', userId],
          context.previousNotifications
        )
      }
    }
  })
}

export const useDeleteNotificationMutation = ({
  userId
}: {
  userId: number
}) => {
  const queryClient = useQueryClient()
  return useMutation<
    boolean,
    Error,
    ReadNotificationPayload,
    { previousNotifications?: InfiniteData<UserNotificationsLDResponse> }
  >({
    mutationFn: ({ notificationId }) =>
      fetchApiWithToken({
        method: 'DELETE',
        url: `/v1/notifications/${notificationId}`
      }),
    onMutate: async ({ notificationId }) => {
      await queryClient.cancelQueries({
        queryKey: ['user', 'notifications', userId]
      })
      const previousNotifications = queryClient.getQueryData<
        InfiniteData<UserNotificationsLDResponse, Error>
      >(['user', 'notifications', userId])
      if (previousNotifications) {
        const updatedNotifications = previousNotifications.pages.map(
          (page) => ({
            ...page,
            'hydra:member': page['hydra:member'].filter(
              (notification) => notification.id !== notificationId
            )
          })
        )
        queryClient.setQueryData(['user', 'notifications', userId], {
          pages: updatedNotifications,
          pageParams: previousNotifications.pageParams
        })
      }
      return { previousNotifications }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['user', 'notifications', userId]
      }),
    onError: async (_error, _variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['user', 'notifications', userId],
          context.previousNotifications
        )
      }
    }
  })
}

export const useReadAllNotifications = ({ userId }: { userId: number }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      return await fetchApiWithToken({
        method: 'post',
        url: '/v1/notifications/read',
        data: {}
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user', 'notifications', userId]
      })
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['user', 'notifications', userId]
      })
      const cachedQueryData = queryClient.getQueryData<
        InfiniteData<UserNotificationsLDResponse, Error>
      >(['user', 'notifications', userId])
      if (cachedQueryData) {
        const updatedNotifications = cachedQueryData.pages.map((page) => ({
          ...page,
          'hydra:member': page['hydra:member'].map((notification) => ({
            ...notification,
            isRead: true
          }))
        }))
        queryClient.setQueryData(['user', 'notifications', userId], {
          pages: updatedNotifications,
          pageParams: cachedQueryData.pageParams
        })
      }
      return { cachedQueryData }
    }
  })
}
